import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Hook to listen for service worker messages and handle cache invalidation
 * Uses Workbox's BroadcastUpdatePlugin for automatic cache invalidation
 */
export const useServiceWorkerMessages = () => {
    const queryClient = useQueryClient();

    // Function to clear module caches when React context issues occur
    const clearModuleCache = useCallback(() => {
        // Don't clear cache if offline - preserve offline functionality
        if (!navigator.onLine) {
            console.warn('🚫 Offline detected - preserving cached modules for offline functionality');
            return;
        }

        if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({
                type: 'CLEAR_MODULE_CACHE'
            });
        }
    }, []);

    // Function to skip waiting for service worker updates
    const skipWaiting = useCallback(() => {
        if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({
                type: 'SKIP_WAITING'
            });
        }
    }, []);

    useEffect(() => {
        const handleServiceWorkerMessage = (event: MessageEvent) => {
            const { type, data } = event.data || {};

            // Add debugging for malformed messages
            if (!type) {
                console.warn('⚠️ Service worker message received without type:', event.data);
                return;
            }

            // Add debugging for CACHE_UPDATED messages to help identify the source (dev only)
            if (type === 'CACHE_UPDATED' && process.env.NODE_ENV === 'development') {
                console.log('🔍 CACHE_UPDATED message received:', {
                    type,
                    data,
                    hasData: 'data' in event.data,
                    dataType: typeof data,
                    meta: event.data?.meta,
                    payload: event.data?.payload,
                    fullEventData: event.data
                });
            }

            switch (type) {
                case 'CACHE_UPDATED':
                    // Workbox BroadcastUpdatePlugin sends this when cache is updated
                    if (process.env.NODE_ENV === 'development') {
                        console.log('🔄 Cache updated from service worker:', data);
                    }

                    // Handle Workbox BroadcastUpdatePlugin message structure
                    // Workbox sends: { type: 'CACHE_UPDATED', meta: 'workbox-broadcast-update', payload: {...} }
                    let cacheName, updatedURL;

                    if (event.data?.meta === 'workbox-broadcast-update' && event.data?.payload) {
                        // Workbox BroadcastUpdatePlugin format
                        const payload = event.data.payload;
                        cacheName = payload.cacheName;
                        updatedURL = payload.updatedURL;
                        if (process.env.NODE_ENV === 'development') {
                            console.log('🔄 Workbox cache update:', { cacheName, updatedURL });
                        }
                    } else if (data && typeof data === 'object') {
                        // Legacy format (if any other source sends this)
                        cacheName = data.cacheName;
                        updatedURL = data.updatedURL;
                        if (process.env.NODE_ENV === 'development') {
                            console.log('🔄 Legacy cache update:', { cacheName, updatedURL });
                        }
                    } else {
                        console.warn('⚠️ CACHE_UPDATED message received with unrecognized structure:', event.data);
                        break;
                    }

                    // Invalidate React Query caches based on which cache was updated
                    if (cacheName === 'user-profile-cache' || updatedURL?.includes('/api/auth/me')) {
                        if (process.env.NODE_ENV === 'development') {
                            console.log('🔄 Invalidating user profile cache');
                        }
                        queryClient.invalidateQueries({ queryKey: ['user'] });
                        queryClient.refetchQueries({ queryKey: ['user'] });
                    }

                    if (cacheName === 'user-limits-cache' || updatedURL?.includes('/api/shopping-lists/user/limits')) {
                        if (process.env.NODE_ENV === 'development') {
                            console.log('🔄 Invalidating user limits cache');
                        }
                        queryClient.invalidateQueries({ queryKey: ['user-limits'] });
                    }
                    break;

                case 'SW_LOG':
                    // Optional: Forward service worker logs to main console
                    if (data && typeof data === 'object' && data.message) {
                        console.log(data.message, ...(data.args || []));
                    } else {
                        console.warn('⚠️ SW_LOG message received with invalid data structure:', data);
                    }
                    break;

                case 'SW_READY':
                    if (process.env.NODE_ENV === 'development') {
                        console.log('✅ Service Worker ready:', data);
                    }
                    break;

                case 'SMART_SYNC_COMPLETE':
                    // Handle background sync completion
                    if (process.env.NODE_ENV === 'development') {
                        console.log('🔄 Background sync completed:', data);
                    }
                    break;

                case 'MODULE_CACHE_CLEARED':
                    // Handle module cache cleared notification
                    if (process.env.NODE_ENV === 'development') {
                        console.log('🧹 Module cache cleared by service worker:', data);
                    }
                    if (data && typeof data === 'object' && data.surgical) {
                        if (process.env.NODE_ENV === 'development') {
                            console.log('✅ Surgical cache clearing completed - offline functionality preserved');
                        }
                        // Don't reload immediately for surgical clearing, let React handle it gracefully
                        // Only reload if we detect actual React context errors
                    } else {
                        // Force a page reload to get fresh modules (for wholesale clearing)
                        setTimeout(() => {
                            window.location.reload();
                        }, 100);
                    }
                    break;

                default:
                    // Ignore unknown message types
                    break;
            }
        };

        // Listen for messages from service worker
        navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

        // Listen for Workbox BroadcastUpdatePlugin messages
        const broadcastChannel = new BroadcastChannel('user-profile-updates');
        broadcastChannel.addEventListener('message', (event) => {
            if (process.env.NODE_ENV === 'development') {
                console.log('🔄 BroadcastUpdatePlugin notification:', event.data);
            }
            // Invalidate user profile cache when broadcast update is received
            queryClient.invalidateQueries({ queryKey: ['user'] });
            queryClient.refetchQueries({ queryKey: ['user'] });
        });

        return () => {
            navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
            broadcastChannel.close();
        };
    }, [queryClient]);

    return {
        clearModuleCache,
        skipWaiting
    };
};
